import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Flame, Gauge, TrendingUp, Plus, X, MessageCircle, Mail, Globe, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { StaffShell } from "@/components/StaffShell";
import { useAuth } from "@/context/AuthContext";
import { Eyebrow } from "@/brand/ui";
import { normQual } from "@/i18n/content";

const STAGES = [
  { key: "lead", label: "Lead" },
  { key: "triagem", label: "Triagem" },
  { key: "qualificacao", label: "Qualificação" },
  { key: "email_preliminar", label: "Email Preliminar" },
  { key: "consulta", label: "Consulta" },
  { key: "documentos", label: "Documentos" },
  { key: "proposta", label: "Proposta" },
  { key: "contratacao", label: "Contratação" },
  { key: "nao_contratado", label: "Não contratado" },
  { key: "follow_up", label: "Follow-up" },
];
const qualColor = { quente: "var(--success)", morno: "var(--warning)", frio: "var(--info)" };
const channelIcon = { whatsapp: MessageCircle, email: Mail, portal: Globe };
const inp = { width: "100%", background: "var(--bg-elevated)", border: "1px solid var(--line)", borderRadius: 2, padding: "11px 13px", fontSize: 14, color: "var(--navy)", fontFamily: "var(--font-body)", outline: "none" };

export default function Dashboard() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inboundOpen, setInboundOpen] = useState(false);
  const [gmail, setGmail] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const [l, s] = await Promise.all([api.get("/leads"), api.get("/stats")]);
    setLeads(l.data); setStats(s.data); setLoading(false);
    try { const g = await api.get("/integrations/gmail/status"); setGmail(g.data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("gmail") === "connected") { toast.success("Gmail ligado com sucesso."); window.history.replaceState({}, "", "/painel"); }
    else if (p.get("gmail") === "erro") { toast.error("Falha ao ligar o Gmail. Tente novamente."); window.history.replaceState({}, "", "/painel"); }
  }, []);

  const connectGmail = () => { window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/oauth/gmail/login`; };

  const move = async (id, stage, e) => { e.stopPropagation(); await api.patch(`/leads/${id}/stage`, { stage }); load(); };

  return (
    <StaffShell>
      <header style={{ height: 68, borderBottom: "1px solid var(--line)", background: "var(--bg-elevated)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 32px", position: "sticky", top: 0, zIndex: 10 }}>
        <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: 26, color: "var(--navy)", margin: 0 }}>Pipeline Comercial</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => navigate("/painel/casos/prototipo")} data-testid="open-case-prototype"
            style={{ display: "flex", alignItems: "center", gap: 8, background: "transparent", color: "var(--silver-dark)", border: "1px solid var(--line)", borderRadius: 2, padding: "11px 16px", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 500, cursor: "pointer" }}>
            Protótipo Casos
          </button>
          {user?.role === "admin" && (gmail?.connected ? (
            <span data-testid="gmail-connected" title={gmail?.email || ""} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--success)", border: "1px solid var(--line)", borderRadius: 2, padding: "11px 16px" }}>
              <Mail size={14} /> Gmail ligado
            </span>
          ) : (
            <button onClick={connectGmail} disabled={!gmail?.configured} data-testid="connect-gmail"
              title={gmail?.configured ? "" : "Faltam credenciais Google (Client ID/Secret)"}
              style={{ display: "flex", alignItems: "center", gap: 8, background: "transparent", color: "var(--navy)", border: "1px solid var(--line)", borderRadius: 2, padding: "11px 16px", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 500, cursor: gmail?.configured ? "pointer" : "not-allowed", opacity: gmail?.configured ? 1 : 0.55 }}>
              <Mail size={14} /> Ligar Gmail
            </button>
          ))}
          <button onClick={() => setInboundOpen(true)} data-testid="open-inbound-simulator"
            style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--navy)", color: "var(--off-white)", border: 0, borderRadius: 2, padding: "12px 20px", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 500, cursor: "pointer" }}>
            <Plus size={16} /> Simular Entrada
          </button>
        </div>
      </header>

      <div style={{ padding: 32, display: "flex", flexDirection: "column", gap: 28 }}>
        <div className="dai-grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }} data-testid="stats-row">
          <Stat icon={Users} label="Total de Leads" value={stats?.total ?? 0} />
          <Stat icon={Flame} label="Leads Quentes" value={stats?.hot_leads ?? 0} accent />
          <Stat icon={Gauge} label="Elegibilidade Média" value={stats?.avg_eligibility ?? 0} suffix="/100" />
          <Stat icon={TrendingUp} label="Conversão" value={stats?.conversion ?? 0} suffix="%" />
        </div>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 80, color: "var(--gold-deep)" }}><Loader2 className="ai-pulse" /></div>
        ) : (
          <div style={{ display: "flex", gap: 14, overflowX: "auto", paddingBottom: 14 }} data-testid="kanban-board">
            {STAGES.map((col) => {
              const items = leads.filter((l) => l.stage === col.key);
              return (
                <div key={col.key} data-testid={`column-${col.key}`} style={{ flex: "0 0 240px", minWidth: 240 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 2px 12px", borderBottom: "1px solid var(--silver-mid)", marginBottom: 14 }}>
                    <span style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--silver-dark)", fontWeight: 600 }}>{col.label}</span>
                    <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--silver-mid)" }}>{items.length}</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {items.map((l) => {
                      const Ch = channelIcon[l.channel] || Globe;
                      return (
                        <div key={l.id} onClick={() => navigate(`/painel/lead/${l.id}`)} data-testid={`lead-card-${l.id}`}
                          style={{ background: "var(--bg-elevated)", border: "1px solid var(--line)", borderRadius: 4, padding: 16, cursor: "pointer", transition: "box-shadow 180ms, transform 180ms" }}
                          onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "var(--shadow-3)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                              <Ch size={14} style={{ color: "var(--gold-deep)", flexShrink: 0 }} />
                              <span style={{ fontWeight: 500, fontSize: 14, color: "var(--navy)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.name}</span>
                            </div>
                            <span style={{ flexShrink: 0, padding: "2px 7px", borderRadius: 999, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#fff", background: qualColor[normQual(l.triage?.qualificacao)] }}>{normQual(l.triage?.qualificacao)}</span>
                          </div>
                          <p style={{ fontSize: 12, color: "var(--silver-dark)", margin: "6px 0 0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.triage?.area}</p>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
                            <div style={{ flex: 1, height: 3, background: "var(--pearl)", borderRadius: 999, overflow: "hidden" }}>
                              <div style={{ height: "100%", width: `${l.triage?.elegibilidade_score || 0}%`, background: "var(--gold-deep)" }} />
                            </div>
                            <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--navy)" }}>{l.triage?.elegibilidade_score || 0}</span>
                          </div>
                          <select value={l.stage} onClick={(e) => e.stopPropagation()} onChange={(e) => move(l.id, e.target.value, e)} data-testid={`move-lead-${l.id}`}
                            style={{ marginTop: 12, width: "100%", background: "var(--paper)", border: "1px solid var(--line)", borderRadius: 2, fontSize: 12, padding: "7px 8px", color: "var(--silver-dark)", outline: "none" }}>
                            {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                          </select>
                        </div>
                      );
                    })}
                    {items.length === 0 && <div style={{ textAlign: "center", color: "var(--silver-mid)", fontSize: 12, padding: 20, border: "1px dashed var(--line)", borderRadius: 4 }}>Vazio</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <InboundModal open={inboundOpen} onClose={() => setInboundOpen(false)} onDone={load} />
    </StaffShell>
  );
}

function Stat({ icon: Icon, label, value, suffix, accent }) {
  return (
    <div style={{ background: "var(--bg-elevated)", border: "1px solid var(--line)", borderRadius: 4, padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--silver-dark)", fontWeight: 500 }}>{label}</span>
        <Icon size={16} style={{ color: accent ? "var(--gold-deep)" : "var(--silver-mid)" }} />
      </div>
      <p style={{ fontFamily: "var(--font-serif)", fontSize: 44, fontWeight: 500, color: "var(--navy)", margin: "12px 0 0", lineHeight: 1 }}>
        {value}<span style={{ fontFamily: "var(--font-mono)", fontSize: 16, color: "var(--silver-mid)" }}>{suffix}</span>
      </p>
    </div>
  );
}

function InboundModal({ open, onClose, onDone }) {
  const [channel, setChannel] = useState("whatsapp");
  const [contact, setContact] = useState("");
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!contact || !message) { toast.error("Contacto e mensagem são obrigatórios."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/inbound", { channel, contact, name, subject, message });
      toast.success(data.new ? `Novo lead captado via ${channel} (${data.ref})` : `Mensagem anexada ao lead ${data.ref}`);
      setContact(""); setName(""); setSubject(""); setMessage(""); await onDone(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (!open) return null;
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,32,64,0.45)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="inbound-modal" className="fade-up" style={{ width: "100%", maxWidth: 520, background: "var(--bg-elevated)", borderRadius: 4, padding: 32, display: "flex", flexDirection: "column", gap: 18, boxShadow: "var(--shadow-4)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <Eyebrow gold withRule={false}>Captação multicanal</Eyebrow>
            <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: 24, color: "var(--navy)", margin: "8px 0 0" }}>WhatsApp / Email</h3>
          </div>
          <button onClick={onClose} data-testid="close-inbound" style={{ background: "transparent", border: 0, color: "var(--silver-dark)", cursor: "pointer" }}><X size={20} /></button>
        </div>
        <p style={{ fontSize: 13, color: "var(--silver-dark)", margin: 0 }}>Simula uma mensagem recebida. A IA faz a triagem e alimenta o mesmo pipeline.</p>
        <div style={{ display: "flex", gap: 10 }}>
          {[["whatsapp", MessageCircle, "WhatsApp"], ["email", Mail, "Email"]].map(([key, Ic, lbl]) => (
            <button key={key} onClick={() => setChannel(key)} data-testid={`channel-${key}`}
              style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px 0", borderRadius: 2, fontSize: 13, cursor: "pointer",
                border: `1px solid ${channel === key ? "var(--navy)" : "var(--line)"}`, background: channel === key ? "var(--navy)" : "transparent", color: channel === key ? "var(--off-white)" : "var(--silver-dark)" }}>
              <Ic size={16} /> {lbl}
            </button>
          ))}
        </div>
        <input value={contact} onChange={(e) => setContact(e.target.value)} data-testid="inbound-contact" placeholder={channel === "whatsapp" ? "+351 912 345 678" : "cliente@email.com"} style={inp} />
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="inbound-name" placeholder="Nome (opcional)" style={inp} />
        {channel === "email" && <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="inbound-subject" placeholder="Assunto" style={inp} />}
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} data-testid="inbound-message" placeholder="Mensagem recebida do cliente…" style={{ ...inp, resize: "vertical" }} />
        <button onClick={submit} disabled={loading} data-testid="submit-inbound"
          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: "var(--navy)", color: "var(--off-white)", border: 0, borderRadius: 2, padding: "14px 0", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 500, cursor: "pointer", opacity: loading ? 0.6 : 1 }}>
          {loading ? <Loader2 size={16} className="ai-pulse" /> : <>Captar e triar <Send size={16} /></>}
        </button>
      </div>
    </div>
  );
}
